import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useCreateMutation } from "../../../api/genericHooks";
import { BookContext } from "../../../contexts/BookContext";
import { theme } from "../../../utils/theme";
import { PlayButton } from "../../Buttons/PlayButton";
import { VoiceClonerData } from "../VoiceClonerModal";
import { previewVoice } from "../../../api/voices";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  voiceClonerData: VoiceClonerData;
  resetModal: () => void;
};

type ConfirmationData = {
  voiceType: "Preset" | "Custom";
  presetVoiceId: string;
  presetVoiceName: string;
  customVoiceId: string;
  customVoiceName: string;
  confirmation: boolean;
};

export const Confirmation: FC<Props> = ({
  open,
  setOpen,
  voiceClonerData,
  resetModal,
}) => {
  const { t } = useTranslation();
  const [sampleAudioUrl, setSampleAudioUrl] = useState<string | null>(null);
  const { refresh } = useContext(BookContext);

  const htmlToText = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.body.textContent || doc.body.innerText || "";
  };

  const { generated_chapters } = useContext(BookContext);
  const sampleTextContent = htmlToText(
    generated_chapters[0]?.content.split(". ").slice(0, 3).join(". ")
  );

  const generateAudioFromText = async (
    voiceId: string
  ): Promise<string | null> => {
    try {
      const audioBlob = await previewVoice(voiceId, sampleTextContent);
      return URL.createObjectURL(audioBlob);
    } catch (error) {
      console.error("Erreur lors de la génération audio:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchAudio = async () => {
      if (voiceClonerData.presetVoiceId || voiceClonerData.customVoiceId) {
        const voiceId =
          voiceClonerData.voiceType == "Preset"
            ? voiceClonerData.presetVoiceId
            : voiceClonerData.customVoiceId;
        if (!voiceId) {
          console.error("Voice ID is not provided");
          return;
        }
        const audioUrl = await generateAudioFromText(voiceId);
        setSampleAudioUrl(audioUrl);
      }
    };
    fetchAudio();
  }, [voiceClonerData]);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ConfirmationData>({
    defaultValues: voiceClonerData as ConfirmationData,
  });

  const confirmAudioBookGenerationMutation = useCreateMutation(
    "/generate_audio_book",
    {
      onSuccess: () => {
        setOpen(false);
        toast.success(t("audioBook.confirmationSuccess"));
        refresh();
      },
      onError: (error) => {
        console.error("Error confirming audio book generation:", error);
      },
    }
  );

  const onSubmit = (data: ConfirmationData) => {
    if (data.confirmation === true) {
      confirmAudioBookGenerationMutation.mutateAsync({
        book: {
          audio_book_config: {
            voice_type: data.voiceType,
            preset_voice_id: data.presetVoiceId,
            preset_voice_name: data.presetVoiceName,
            custom_voice_id: data.customVoiceId,
            custom_voice_name: data.customVoiceName,
          },
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-4 pb-4 pt-5 text-left w-full max-w-sm sm:w-full sm:max-w-xl shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon aria-hidden="true" className="size-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <DialogTitle
                    as="h3"
                    className="text-2xl font-semibold text-slate-800"
                  >
                    {t("audioBook.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />
                </div>
              </div>
              <div className="flex flex-col mt-5">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-2 sm:space-y-0">
                  <p className="text-sm sm:text-md">
                    {t("audioBook.confirmVoiceDescription")}
                  </p>
                </div>
                <div className="flex flex-row items-center justify-center">
                  {sampleAudioUrl ? (
                    <div className="flex flex-col sm:flex-row items-center w-full sm:w-1/3 px-3 sm:px-4 py-2 border-2 rounded space-y-2 sm:space-y-0 sm:space-x-2">
                      <PlayButton audioUrl={sampleAudioUrl} />
                      <p className="text-center text-xs sm:text-sm">
                        {voiceClonerData.presetVoiceName}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col w-full items-center justify-center">
                      <BeatLoader color={theme.colors.delicate[200]} />
                      <p className="text-center text-xs sm:text-sm mt-2">
                        {t("audioBook.loadingVoiceSample")}
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <label className="flex items-start sm:items-center">
                    <input
                      type="checkbox"
                      {...register("confirmation", {
                        required: t("audioBook.selectVoiceError"),
                      })}
                      className="mr-2 mt-0.5 sm:mt-0 h-4 w-4 rounded border-gray-300 text-rust-500 focus:ring-rust-500 flex-shrink-0"
                    />
                    <span className="text-xs sm:text-sm">
                      {t("audioBook.confirmVoiceSelection")}
                    </span>
                  </label>
                </div>
                {errors && errors.confirmation && (
                  <p className="text-red-500 text-xs sm:text-sm mt-2">
                    {t("audioBook.confirmAudioBookGenerationError")}
                  </p>
                )}
              </div>

              <hr className="mt-6 sm:mt-8 w-full border-gray-200" />
              <div className="mt-2 flex w-full flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0 sm:space-x-2">
                <button
                  type="button"
                  onClick={() => resetModal()}
                  className="w-full sm:w-1/2 inline-flex justify-center rounded hover:bg-rust-50 text-rust-50 px-3 py-2 text-sm font-semibold hover:text-white shadow-sm ring-1 ring-inset ring-rust-300 bg-white"
                >
                  {t("restart")}
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-1/2 inline-flex justify-center rounded bg-rust-50 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100"
                >
                  {t("next")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
